import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../../pages/Api';

const HappinessIndexTest = () => {
    const [allData, setAllData] = useState([]);
    const [activeTabIdx, setActiveTabIdx] = useState(-1); 
    const [scores, setScores] = useState({}); 
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await API.get('get-happiness-test/');
                const data = response.data;
                if (data && data.length > 0) {
                    setAllData(data);
                }
                setLoading(false);
            } catch (error) {
                console.error("API Error:", error);
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    
    const handleScaleChange = (qId, value) => {
        const val = Math.min(10, Math.max(0, parseInt(value) || 0));
        setScores(prev => ({ ...prev, [qId]: val }));
    };

    const getResultConfig = (score, maxScore) => {
        const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;

        if (percentage >= 80) return { color: 'bg-green-600', label: 'Good', msg: 'Great job! Keep maintaining this index.' };
        if (percentage >= 60) return { color: 'bg-blue-400', label: 'Normal', msg: 'You are doing fine, but there is room for improvement.' };
        if (percentage >= 40) return { color: 'bg-yellow-400', label: 'Weak', msg: 'You are not serious about this. You need guidance!' };
        return { color: 'bg-red-600', label: 'Danger', msg: 'Critical condition! You need urgent professional guidance.' };
    };

    const calculateTotal = () => {
        return Object.values(scores).reduce((a, b) => a + b, 0);
    };

    const handleSubmit = async () => {
        const totalScore = calculateTotal();

        const payload = {
            scores: scores,
            total_score: totalScore 
        };

        try {
            const response = await API.post('save-test-result/', payload);

            if (response.status === 200 || response.status === 201) {
                toast.success("Test submitted successfully!");
                navigate('/TGSH/user/dashboard');
            } else {
                console.error("Server Error Detail:", response.data);
                toast.error(`Error: ${response.data?.error || "Data could not be saved."}`);
            }
        } catch (error) {
            console.error("Network Error:", error);
            const errorMsg = error.response?.data?.error || error.response?.data?.detail || "Could not contact the server..";
            toast.error(errorMsg);
        }
    };

    if (loading) return <div className="p-10 text-center font-bold text-blue-900">Loading Questions...</div>;

    const currentCategory = allData[activeTabIdx];

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            {/* Header */}
            <div className="max-w-6xl mx-auto mb-4 md:mb-8 bg-white p-3 md:p-6 rounded-xl border-2 border-blue-900 shadow-sm">
                <h2 className="text-3xl font-normal text-blue-900 mb-6 text-center uppercase ">Happiness Index</h2>
                <div className="flex flex-wrap gap-1 md:gap-2 justify-center">
                    <button onClick={() => setActiveTabIdx(-1)} className={`px-2 md:px-6 py-2 text-[10px] md:text-sm rounded-md font-semibold border transition-all ${activeTabIdx === -1 ? 'bg-blue-900 text-white' : 'bg-white text-blue-900 border-blue-900'}`}>INSTRUCTIONS</button>
                    {allData.map((item, idx) => (
                        <button key={idx} onClick={() => setActiveTabIdx(idx)} className={`px-2 md:px-6 py-2 text-[10px] md:text-sm rounded-md font-semibold border transition-all ${activeTabIdx === idx ? 'bg-blue-900 text-white' : 'bg-white text-blue-900 border-blue-900'}`}>{item.title_name}</button>
                    ))}
                    <button onClick={() => setActiveTabIdx(allData.length)} className={`px-2 md:px-6 py-2 text-[10px] md:text-sm rounded-md font-semibold border transition-all ${activeTabIdx === allData.length ? 'bg-blue-900 text-white' : 'bg-white text-blue-900 border-blue-900'}`}>RESULT</button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-6xl mx-auto bg-white p-3 md:p-8 rounded-xl border border-gray-300 shadow-lg min-h-[500px]">
                
                {activeTabIdx === -1 ? (
                    /* 1. INSTRUCTIONS */
                    <div className="space-y-4 text-gray-700">
                        <p className="font-bold text-blue-900 text-lg">Answer the questions honestly to achieve PEAK HAPPINESS INDEX!</p>
                        <p>The detailed analysis of your score in this test will give you a clear idea of the areas to focus in your life to achieve PEAK HAPPINESS INDEX!</p>

                        <div className="bg-gray-50 p-4 border-l-4 border-blue-900 font-bold text-blue-900">
                            HAPPINESS INDEX QUESTIONAIRE
                        </div>
                        <p className="font-semibold italic">Rate every question one to ten (1-10)</p>
                        <p>One means lowest performance or correctness and Ten means highest performance or most correct</p>
                        <p>Example : - I do not have diabetes</p>
                        <p>1-3 means I have severe diabetes | 4-7 means moderate | 7-9 means pre diabetic | 10 means I do not have diabetes</p>

                        <div className="pt-6 text-center">
                            {allData.length > 0 ? (
                                <button onClick={() => setActiveTabIdx(0)} className="bg-blue-900 text-white px-10 py-3 rounded-lg font-bold shadow-md hover:bg-blue-800 transition">Start Test</button>
                            ) : (
                                <p className="text-red-500 font-bold italic">The questionnaire could not be loaded. Please check the internet..</p>
                            )}
                        </div>
                    </div>

                ) : activeTabIdx === allData.length ? (
                    /* 2. RESULT TABLE */
                    <div className="space-y-6">
                        <h3 className="text-center text-blue-900 font-bold text-2xl uppercase border-b pb-4">Result As Per the Marking</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-[700px] md:w-full border-collapse border border-gray-300 text-[13px]">
                                <thead>
                                    <tr className="bg-blue-900 text-white font-bold uppercase">
                                        <th className="border border-gray-300 p-3 text-left">Title</th>
                                        <th className="border border-gray-300 p-3 w-20">Status</th>
                                        <th className="border border-gray-300 p-3 text-left">Conclusion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allData.map((category) => {
                                        const catScore = category.questions.reduce((sum, q) => sum + (scores[q.questionid] || 0), 0);
                                        const maxCatScore = category.questions.length * 10;
                                        const res = getResultConfig(catScore, maxCatScore);
                                        return (
                                            <tr key={category.tileid} className="hover:bg-gray-50 transition-colors">
                                                <td className="border border-gray-200 p-3 font-bold text-blue-900 uppercase">{category.title_name}</td>
                                                <td className="border border-gray-200 p-3 text-center">
                                                    <div className={`w-6 h-6 mx-auto ${res.color} border border-gray-400 shadow-sm`}></div>
                                                </td>
                                                <td className="border border-gray-200 p-3 text-gray-600 italic font-medium">{res.msg}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Legend */}
                        <div className="mt-8 flex flex-wrap gap-6 justify-start items-center bg-gray-50 p-4 rounded border border-gray-200">
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-700"><div className="w-5 h-5 bg-green-600"></div> GOOD</div>
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-700"><div className="w-5 h-5 bg-blue-400"></div> NORMAL</div>
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-700"><div className="w-5 h-5 bg-yellow-400"></div> WEAK</div>
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-700"><div className="w-5 h-5 bg-red-600"></div> DANGER</div>
                        </div>

                        <div className="mt-10 flex flex-col items-center gap-4">
                            <p className="text-lg md:text-2xl font-black text-blue-900 text-center">Total Happiness Score: {calculateTotal()}</p>
                            <button onClick={handleSubmit} className="bg-blue-900 text-white px-14 py-3 rounded-xl font-bold uppercase shadow-xl hover:bg-blue-800 transition">Submit & Finish</button>
                        </div>
                    </div>

                ) : (
                    /* 3. QUESTIONS LIST */
                    <div className="animate-in slide-in-from-right-4 duration-300">
                        <div className="text-right text-[11px] text-gray-500 font-semibold mb-2">Scale: 0 (Lowest) to 10 (Highest)</div>
                        
                        
                        <div className="h-[60vh] md:h-[400px] overflow-y-auto border border-gray-200 shadow-inner">
                            <div className="overflow-x-auto">
                                <table className="min-w-[600px] md:w-full text-[13px] border-collapse">
                                <thead className="bg-blue-900 text-white ">
                                    <tr className="bg-blue-900 text-white font-bold uppercase">
                                        <th className="border border-gray-300 p-3 w-16">Q.No</th>
                                        <th className="border border-gray-300 p-3 text-center">Particulars</th>
                                        <th className="border border-gray-300 p-3 w-24 text-center">Scale</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentCategory?.questions.map((q, i) => (
                                        <tr key={q.questionid} className="hover:bg-blue-50 transition-colors">
                                            <td className="border border-gray-200 p-3 text-center font-bold">{i + 1}.</td>
                                            <td className="border border-gray-200 p-2 md:p-3 px-2 md:px-6 text-gray-700 font-medium text-[11px] md:text-[13px]">{q.question}</td>
                                            <td className="border border-gray-200 p-3 text-center">
                                                <input 
                                                    type="number" 
                                                    min="0" 
                                                    max="10" 
                                                    value={scores[q.questionid] || ''} 
                                                    onChange={(e) => handleScaleChange(q.questionid, e.target.value)} 
                                                    className="w-16 p-2 border border-blue-200 rounded text-center font-bold focus:ring-2 focus:ring-blue-900 outline-none" 
                                                    placeholder="0" 
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                </table>
                            </div>
                        </div>

                        
                        <div className="mt-8 flex flex-col md:flex-row justify-center gap-3">
                            <button onClick={() => setActiveTabIdx(prev => Math.max(-1, prev - 1))} className="w-full md:w-auto px-8 py-2 bg-gray-200 text-gray-700 rounded font-bold uppercase hover:bg-gray-300 transition-all">Previous</button>
                            <button onClick={() => setActiveTabIdx(prev => prev + 1)} className="w-full md:w-auto px-8 py-2 bg-blue-900 text-white rounded font-bold uppercase hover:bg-blue-800 transition-all shadow-lg">Save & Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HappinessIndexTest;